import { useEffect, useState } from "react";
import { apiPost } from "../lib/api";
import type { DraftBody } from "../types/gap";

interface Props {
  open: boolean;
  onClose: () => void;
  gapId: string;
  recipient: "sacristan" | "shortlist";
  recipientId?: string;
}

export default function DraftPanel({ open, onClose, gapId, recipient, recipientId }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [recipientLine, setRecipientLine] = useState<string | undefined>();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    setCopied(false);
    apiPost<DraftBody>("/drafts/email", { gapId, recipient, recipientId })
      .then((res) => {
        setSubject(res.draft.subject ?? "");
        setBody(res.draft.body);
        setRecipientLine(res.draft.recipient);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [open, gapId, recipient, recipientId]);

  if (!open) return null;

  return (
    <div className="draft-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="draft-panel" onClick={(e) => e.stopPropagation()}>
        <div className="draft-panel__header">
          <h2 className="draft-panel__title">Draft email</h2>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Close
          </button>
        </div>
        {loading ? (
          <p className="placeholder">Drafting…</p>
        ) : error ? (
          <p className="placeholder">Couldn't draft: {error}</p>
        ) : (
          <>
            {recipientLine ? (
              <div className="draft-panel__field">
                <label className="draft-panel__label">To</label>
                <div className="draft-panel__static">{recipientLine}</div>
              </div>
            ) : null}
            <div className="draft-panel__field">
              <label className="draft-panel__label" htmlFor="draft-subject">
                Subject
              </label>
              <input
                id="draft-subject"
                className="draft-panel__input"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>
            <div className="draft-panel__field">
              <label className="draft-panel__label" htmlFor="draft-body">
                Message
              </label>
              <textarea
                id="draft-body"
                className="draft-panel__textarea"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={12}
              />
            </div>
            <div className="draft-panel__actions">
              <button
                type="button"
                className="btn"
                onClick={async () => {
                  const text = subject ? `Subject: ${subject}\n\n${body}` : body;
                  await navigator.clipboard.writeText(text);
                  setCopied(true);
                }}
              >
                {copied ? "Copied ✓" : "Copy to clipboard"}
              </button>
              <button type="button" className="btn btn--ghost" onClick={onClose}>
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
